import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Grid, CircularProgress, Alert, Typography,
  Table, TableHead, TableRow, TableCell, TableBody,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { useHistory, useParams } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import SectionCard from '../../components/SectionCard';
import StatusChip from '../../components/StatusChip';
import InvButton from '../../components/InvButton';
import BatchChip from '../../components/BatchChip';
import { DetailRow } from '../../components/InvField';
import { getTransferDetail, getLedger, getStores } from '../../api/stockLedger';
import { getUoms } from '../../api/inventoryMaster';
import { storeDisplayName } from '../../utils/storeEntity';
import { resolveActorLabel } from '../../hooks/useStoreScope';
import useItemNames from '../../hooks/useItemNames';
import { normalizeUomList } from '../../utils/masterEntity';
import { isUuidLike } from '../../utils/itemDisplay';
import { formatDate, formatDateTime, formatCurrency, formatQty } from '../../utils';
import { TRANSFER_TIMELINE_STEPS } from '../../constants/stockTransfer';
import { IPD_COLORS } from '../../theme';

const LEDGER_PAGE_LIMIT = 200;

function normalizeList(data) {
  if (Array.isArray(data)) return data;
  return data?.items || data?.data || [];
}

function buildUomMap(uoms) {
  const map = {};
  (Array.isArray(uoms) ? uoms : []).forEach((u) => {
    const id = u.uom_id || u.id;
    if (id) map[id] = u.uom_name || u.uom_code || u.name || '';
  });
  return map;
}

function resolveUomLabel(line, uomMap, getBaseUomCode) {
  if (line?.uom_name && !isUuidLike(line.uom_name)) return line.uom_name;
  if (line?.uom_code && !isUuidLike(line.uom_code)) return line.uom_code;
  if (line?.uom_id && uomMap[line.uom_id]) return uomMap[line.uom_id];
  return getBaseUomCode?.(line?.item_id) || '—';
}

function batchKey(itemId, batchNumber) {
  return `${itemId || ''}::${batchNumber || ''}`;
}

/**
 * On-hand quantity in the source store immediately before this transfer.
 *
 * The transaction line does not record it, but its TRANSFER_OUT ledger entry does:
 * balance_after is the level once the stock left, so adding back what left gives
 * the level at the time of transfer.
 */
function buildStockAtTransfer(ledgerRows) {
  const map = new Map();
  ledgerRows.forEach((row) => {
    if (row.movement_type !== 'TRANSFER_OUT') return;
    const before = Number(row.balance_after) + Number(row.quantity_out);
    if (!Number.isFinite(before)) return;
    map.set(batchKey(row.item_id, row.batch_number), before);
  });
  return map;
}

/**
 * Status-driven step indicator — same circle/connector technique as the requisition
 * timeline. A transfer posts stock in one request, so a COMPLETED transaction has
 * already passed every step; there is no dispatch/receive handoff to wait on.
 */
function TransferTimeline({ status }) {
  const terminal = status === 'CANCELLED' || status === 'REVERSED' ? status : null;
  const currentIndex = terminal ? 0 : TRANSFER_TIMELINE_STEPS.length - 1;

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', overflowX: 'auto', pb: 0.5, mb: 2.5 }}>
      {TRANSFER_TIMELINE_STEPS.map((label, i) => {
        const isTerminalHere = terminal && i === currentIndex;
        const isDone = !terminal && i <= currentIndex;
        const isLast = i === TRANSFER_TIMELINE_STEPS.length - 1;

        let circleBg = IPD_COLORS.cardBg;
        let circleBorder = IPD_COLORS.border;
        let circleColor = IPD_COLORS.primary;
        let stepLabel = label;

        if (isTerminalHere) {
          circleBg = IPD_COLORS.error.text;
          circleBorder = IPD_COLORS.error.text;
          circleColor = '#fff';
          stepLabel = terminal.replace(/_/g, ' ');
        } else if (isDone) {
          circleBg = IPD_COLORS.primary;
          circleBorder = IPD_COLORS.primary;
          circleColor = '#fff';
        }

        return (
          <Box key={label} sx={{ display: 'flex', alignItems: 'flex-start', flexShrink: 0 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: { xs: 88, sm: 110 } }}>
              <Box sx={{
                width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center',
                justifyContent: 'center', border: `2px solid ${circleBorder}`, background: circleBg,
                color: circleColor, fontWeight: 700, fontSize: 12, flexShrink: 0,
              }}>
                {isDone ? <CheckIcon sx={{ fontSize: 16 }} /> : i + 1}
              </Box>
              <Typography sx={{
                mt: 1, fontSize: 10.5, fontWeight: isTerminalHere || isLast ? 700 : 600,
                color: isTerminalHere ? IPD_COLORS.error.text : isDone ? IPD_COLORS.primaryDark : IPD_COLORS.textSecondary,
                textAlign: 'center', lineHeight: 1.25, px: 0.5,
              }}>
                {stepLabel}
              </Typography>
            </Box>
            {!isLast && (
              <Box sx={{
                height: 2, width: { xs: 16, sm: 28 }, mt: '15px',
                backgroundColor: !terminal && i < currentIndex ? IPD_COLORS.primary : IPD_COLORS.border,
              }} />
            )}
          </Box>
        );
      })}
    </Box>
  );
}

export default function TransferDetail() {
  const { id } = useParams();
  const history = useHistory();
  const { resolveItemId, getBaseUomCode } = useItemNames();
  const [txn, setTxn] = useState(null);
  const [stockAtTransfer, setStockAtTransfer] = useState(new Map());
  const [stores, setStores] = useState([]);
  const [uomMap, setUomMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // API: GET /inventory/uoms/
    getUoms({ is_active: true, limit: 200 })
      .then((d) => setUomMap(buildUomMap(normalizeUomList(d))))
      .catch(() => setUomMap({}));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [detail, ledger, storesData] = await Promise.all([
        // API: GET /inventory/inventory-transactions/{id}/
        getTransferDetail(id),
        // API: GET /inventory/stock/ledger/ — TRANSFER_OUT rows of this transaction
        getLedger({
          reference_type: 'INVENTORY_TRANSACTION',
          reference_id: id,
          movement_type: 'TRANSFER_OUT',
          limit: LEDGER_PAGE_LIMIT,
        }).catch((e) => {
          console.error('[TransferDetail] load ledger failed:', e);
          return [];
        }),
        // API: GET /inventory/stores/
        getStores().catch((e) => {
          console.error('[TransferDetail] load stores failed:', e);
          return [];
        }),
      ]);
      setTxn(detail);
      setStockAtTransfer(buildStockAtTransfer(normalizeList(ledger)));
      setStores(normalizeList(storesData));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const resolveStoreName = useCallback((storeId) => {
    if (!storeId) return '—';
    const store = stores.find(s => (s.store_id || s.id) === storeId);
    return store ? storeDisplayName(store) : storeId;
  }, [stores]);

  const lines = useMemo(() => (Array.isArray(txn?.lines) ? txn.lines : []), [txn]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress sx={{ color: IPD_COLORS.primary }} />
      </Box>
    );
  }
  if (error) return <Box sx={{ p: 4 }}><Alert severity="error">{error}</Alert></Box>;
  if (!txn) return null;

  const payload = txn.transaction_payload || {};
  const expectedDate = txn.expected_date || payload.expected_date;
  const transferReason = txn.transfer_reason || payload.transfer_reason;

  return (
    <Box>
      <PageHeader
        eyebrow="Inventory Management › Stock Transfers"
        title={txn.transaction_number || 'Stock Transfer'}
        actions={(
          <InvButton variant="outlined" tone="teal" size="small" onClick={() => history.goBack()}>
            Back
          </InvButton>
        )}
      />

      <TransferTimeline status={txn.status} />

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={5}>
          <SectionCard title="Transfer Details" actions={<StatusChip status={txn.status} />}>
            <DetailRow label="Transfer #">{txn.transaction_number}</DetailRow>
            <DetailRow label="From Store">{resolveStoreName(txn.source_store_id)}</DetailRow>
            <DetailRow label="To Store">{resolveStoreName(txn.destination_store_id)}</DetailRow>
            <DetailRow label="Created Date">{formatDateTime(txn.created_at)}</DetailRow>
            <DetailRow label="Expected Date">{expectedDate ? formatDate(expectedDate) : '—'}</DetailRow>
            <DetailRow label="Created By">{resolveActorLabel(txn.created_by)}</DetailRow>
            <DetailRow label="Reason">{transferReason || '—'}</DetailRow>
            <DetailRow label="Remarks">{txn.remarks || '—'}</DetailRow>
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={7}>
          <SectionCard title={`Items Transferred (${lines.length})`}>
            <Box sx={{ overflowX: 'auto', width: '100%', WebkitOverflowScrolling: 'touch' }}>
              <Table
                size="small"
                sx={{
                  minWidth: 640,
                  '& td, & th': { borderColor: IPD_COLORS.border, color: IPD_COLORS.textPrimary },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary, minWidth: 180 }}>Item</TableCell>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Batch</TableCell>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Expiry</TableCell>
                    <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Qty</TableCell>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary }}>UOM</TableCell>
                    <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Sale Rate/unit</TableCell>
                    <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Stock at Transfer</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lines.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ textAlign: 'center', color: IPD_COLORS.textSecondary }}>
                        No items
                      </TableCell>
                    </TableRow>
                  ) : lines.map((line) => {
                    const before = stockAtTransfer.get(batchKey(line.item_id, line.batch_number));
                    return (
                      <TableRow key={line.id}>
                        <TableCell>{resolveItemId(line.item_id)}</TableCell>
                        <TableCell>
                          <BatchChip batchNumber={line.batch_number} expiryDate={line.expiry_date} />
                        </TableCell>
                        <TableCell>{line.expiry_date ? formatDate(line.expiry_date) : '—'}</TableCell>
                        <TableCell align="right">{formatQty(line.issued_quantity)}</TableCell>
                        <TableCell>{resolveUomLabel(line, uomMap, getBaseUomCode)}</TableCell>
                        <TableCell align="right">
                          {Number(line.unit_rate) > 0 ? formatCurrency(line.unit_rate) : '—'}
                        </TableCell>
                        <TableCell align="right">
                          {before != null ? formatQty(before) : '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
            <Typography sx={{ mt: 1.5, fontSize: 12, color: IPD_COLORS.textSecondary }}>
              One row per batch the transfer drew from — batches are picked automatically
              by earliest expiry. Stock at Transfer is the source store&apos;s on-hand
              quantity for that batch just before the stock moved.
            </Typography>
          </SectionCard>
        </Grid>
      </Grid>
    </Box>
  );
}
